import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { lightTheme, darkTheme, createAppTheme } from '../theme';

// Theme context type
interface ThemeContextType {
  mode: 'light' | 'dark';
  savedMode: 'light' | 'dark'; // Track the saved mode from database
  toggleTheme: () => void;
  setThemeMode: (mode: 'light' | 'dark') => void;
  saveThemeToDatabase: () => Promise<void>; // Explicit save method
  hasUnsavedThemeChanges: boolean; // True when current differs from saved
  currentUser: { id: string } | null;
  setCurrentUser: (user: { id: string } | null) => void;
}

// Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

// Theme provider component
interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  // Track current user for Firestore persistence
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null);
  const [initialized, setInitialized] = useState(false);
  
  // Track both current theme and saved theme from database
  const [savedMode, setSavedMode] = useState<'light' | 'dark'>('light');
  
  // Initialize theme mode from localStorage or default to light
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedLocalMode = localStorage.getItem('theme-mode');
    const initialMode = (savedLocalMode === 'light' || savedLocalMode === 'dark') ? savedLocalMode : 'light';
    setSavedMode(initialMode); // Initialize saved mode same as local
    return initialMode;
  });

  // Calculate if there are unsaved theme changes
  const hasUnsavedThemeChanges = mode !== savedMode;

  // Load theme from Firestore when user is set
  useEffect(() => {
    const loadThemeFromFirestore = async () => {
      if (currentUser && !initialized) {
        try {
          const { FirestoreService } = await import('../services/firestoreService');
          const preferences = await FirestoreService.getUserPreferences(currentUser.id);
          if (preferences?.theme) {
            console.log('🎨 ThemeContext: Loading saved theme from Firestore:', preferences.theme);
            setMode(preferences.theme);
            setSavedMode(preferences.theme); // Update saved mode from database
            localStorage.setItem('theme-mode', preferences.theme);
          }
          setInitialized(true);
        } catch (error) {
          console.error('❌ ThemeContext: Failed to load theme from Firestore:', error);
          setInitialized(true);
        }
      } else if (!currentUser) {
        // Reset initialization flag when user logs out
        setInitialized(false);
      }
    };

    loadThemeFromFirestore();
  }, [currentUser, initialized]);

  // Update localStorage when mode changes
  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  // Toggle between light and dark modes (no database save)
  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  };

  // Set specific theme mode (no database save - only UI change)
  const setThemeMode = (newMode: 'light' | 'dark') => {
    setMode(newMode);
    console.log('🎨 ThemeContext: Theme changed locally to:', newMode);
  };

  // Save current theme mode to database (explicit save)
  const saveThemeToDatabase = async () => {
    if (currentUser) {
      try {
        const { FirestoreService } = await import('../services/firestoreService');
        const preferences = await FirestoreService.getUserPreferences(currentUser.id);
        await FirestoreService.saveUserPreferences(currentUser.id, {
          ...preferences,
          theme: mode,
        });
        setSavedMode(mode); // Update saved mode to match current
        console.log('🎨 ThemeContext: Theme saved to database:', mode);
      } catch (error) {
        console.error('❌ ThemeContext: Failed to save theme to database:', error);
        throw error;
      }
    }
  };

  // Create theme based on current mode
  const currentTheme = createAppTheme(mode);

  const value: ThemeContextType = {
    mode,
    savedMode,
    toggleTheme,
    setThemeMode,
    saveThemeToDatabase,
    hasUnsavedThemeChanges,
    currentUser,
    setCurrentUser,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={currentTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;